/**
 * useEmailFetch — fetches Gmail metadata for the selected date range via the
 * backend Gmail proxy. Checks backend cache first for team member views; only
 * hits Gmail on miss.
 *
 * No Google token is involved on this side: `fetch` reads the caller's own
 * mailbox, `fetchForMember` names a reportee and the server authorises it.
 */

import { useState, useCallback } from 'react';
import { fetchEmailMetadata } from '../engine/gmailClient';
import { getMemberCache, saveMemberCache } from '../engine/backendClient';

function toDateStr(date) {
  return date instanceof Date
    ? date.toISOString().split('T')[0]
    : String(date).split('T')[0];
}

export function useEmailFetch() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ fetched: 0, total: 0 });
  const [error, setError] = useState(null);
  const [fromCache, setFromCache] = useState(false);

  /** Fetch for the current user — always fresh from Gmail. */
  const fetch = useCallback(async (from, to) => {
    setLoading(true);
    setError(null);
    setData(null);
    setFromCache(false);
    setProgress({ fetched: 0, total: 0 });

    try {
      const result = await fetchEmailMetadata(null, from, to, (fetched, total) => {
        setProgress({ fetched, total });
      });
      setData(result);
      return {};
    } catch (err) {
      setError(err.message || 'Failed to fetch emails.');
      return { tokenExpired: !!err.tokenExpired || err.status === 410 };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch for a team member — checks backend cache first.
   * Cache hit → instant render. Cache miss → Gmail fetch + save to cache.
   *
   * Returns `{ tokenExpired: true }` when the member's Google grant is gone, so
   * the team list can flag that card. Previously this was detected up front by
   * fetching their token; there is no such endpoint any more, so it surfaces
   * here instead.
   */
  const fetchForMember = useCallback(async (memberId, from, to, forceRefresh = false) => {
    setLoading(true);
    setError(null);
    setData(null);
    setFromCache(false);
    setProgress({ fetched: 0, total: 0 });

    const fromDate = toDateStr(from);
    const toDate = toDateStr(to);

    try {
      if (!forceRefresh) {
        const cacheRes = await getMemberCache(memberId, fromDate, toDate);
        if (cacheRes.cached && cacheRes.metrics) {
          setData(cacheRes.metrics);
          setFromCache(true);
          setLoading(false);
          return {};
        }
      }

      const result = await fetchEmailMetadata(memberId, from, to, (fetched, total) => {
        setProgress({ fetched, total });
      });
      setData(result);

      saveMemberCache(memberId, fromDate, toDate, result).catch((err) =>
        console.warn('[useEmailFetch] Failed to save member cache:', err.message)
      );
      return {};
    } catch (err) {
      setError(err.message || 'Failed to fetch emails.');
      return { tokenExpired: !!err.tokenExpired || err.status === 410 };
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setFromCache(false);
    setProgress({ fetched: 0, total: 0 });
  }, []);

  return { data, loading, progress, error, fromCache, fetch, fetchForMember, reset };
}
